import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { FadeIn } from '@/components/common/FadeIn';
import { SectionLabel } from '@/components/common/SectionLabel';
import { OphirButton } from '@/components/common/OphirButton';
import { Icon } from '@/components/common/Icon';
import { ProcessDiagram } from '@/components/graphics/ProcessDiagram';
import { MarketsMap } from '@/components/graphics/MarketsMap';
import { useContent } from '@/context/ContentContext';
import { HOMEPAGE_B2B } from '@/data/b2b';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';

export default function Home() {
  const { content } = useContent();
  if (!content) return <div className="container-ophir py-24" />;
  const { hero, intro, trust = [], capabilities = [], categories = [], process = [], responsible = {}, faqs = [], seo } = content;

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'OPHIR SUPPLY',
    description: seo?.home?.description,
    slogan: 'Reliable Supply. Valuable Partnerships.',
    areaServed: ['Canada', 'United States'],
    url: window.location.origin,
  };

  return (
    <>
      <Seo title={seo?.home?.title} description={seo?.home?.description} jsonLd={orgJsonLd} />

      {/* HERO */}
      <section className="bg-ophir-ivory-soft">
        <div className="container-ophir py-14 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <FadeIn>
                <SectionLabel>{hero?.descriptor}</SectionLabel>
                <h1 className="mt-5 font-serif text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.06] tracking-[-0.01em] text-ophir-navy-deep">
                  {hero?.headline_line1}
                  <br />
                  <span className="text-ophir-navy">{hero?.headline_line2}</span>
                </h1>
                <p className="mt-6 text-[17px] lg:text-[18px] leading-relaxed text-ophir-muted max-w-[60ch]">
                  {hero?.body}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <OphirButton to={hero?.primary_cta?.href || '/suppliers'} data-testid="hero-primary-cta">
                    {hero?.primary_cta?.label}
                  </OphirButton>
                  <OphirButton variant="secondary" to={hero?.secondary_cta?.href || '/capabilities'} data-testid="hero-secondary-cta">
                    {hero?.secondary_cta?.label}
                  </OphirButton>
                </div>
              </FadeIn>
            </div>
            <div className="lg:col-span-6">
              <FadeIn delay={0.1}>
                <div className="overflow-hidden rounded-2xl border border-ophir-grey shadow-ophir">
                  <img
                    src="/images/ophir/home/hero-staging.webp"
                    width={1376}
                    height={768}
                    alt="A supply professional reviewing inventory documentation beside neatly organised cartons on pallets in a clean receiving area"
                    className="w-full h-full object-cover"
                    fetchpriority="high"
                  />
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-ophir-grey bg-white">
        <div className="container-ophir py-6">
          <ul className="flex flex-wrap items-center justify-center lg:justify-between gap-x-8 gap-y-4">
            {trust.map((t, i) => (
              <li key={i} className="flex items-center gap-2.5 text-ophir-navy">
                <Icon name={t.icon} size={18} className="text-ophir-gold-deep" />
                <span className="text-[13px] font-semibold uppercase tracking-[0.08em]">{t.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-ophir-ivory-soft">
        <div className="container-ophir py-16 lg:py-24">
          <FadeIn className="max-w-[72ch]">
            <SectionLabel>{intro?.label}</SectionLabel>
            <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] lg:text-[44px] leading-[1.15] text-ophir-navy-deep">
              {intro?.heading}
            </h2>
            <p className="mt-6 text-[17px] lg:text-[18px] leading-relaxed text-ophir-muted">{intro?.body}</p>
            <blockquote className="mt-8 border-l-2 border-ophir-gold pl-5 font-serif italic text-[20px] lg:text-[22px] text-ophir-navy">
              {intro?.pullquote}
            </blockquote>
          </FadeIn>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="bg-white border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <FadeIn className="max-w-2xl">
            <SectionLabel>What we do</SectionLabel>
            <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] lg:text-[42px] leading-tight text-ophir-navy-deep">Core capabilities</h2>
          </FadeIn>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {capabilities.map((c, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="h-full bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-7 shadow-ophir hover:shadow-ophir-hover hover:-translate-y-0.5 transition-shadow duration-200">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-ophir-navy/5 text-ophir-navy">
                    <Icon name={c.icon} size={22} />
                  </span>
                  <h3 className="mt-5 font-serif text-[20px] text-ophir-navy-deep">{c.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-ophir-muted">{c.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORY PREVIEW */}
      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <FadeIn className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="max-w-2xl">
              <SectionLabel>Product categories</SectionLabel>
              <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] lg:text-[42px] leading-tight text-ophir-navy-deep">A broad, considered product focus</h2>
            </div>
            <Link to="/categories" className="inline-flex items-center gap-2 text-ophir-navy font-semibold text-[15px] hover:text-ophir-navy-deep">
              View product categories <ArrowRight size={18} />
            </Link>
          </FadeIn>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {categories.slice(0, 12).map((cat, i) => (
              <FadeIn key={cat.slug} delay={(i % 4) * 0.05}>
                <Link to={`/categories/${cat.slug}`} className="group block bg-white border border-ophir-grey rounded-2xl overflow-hidden shadow-ophir hover:shadow-ophir-hover transition-shadow duration-200">
                  <div className="aspect-[4/3] overflow-hidden bg-ophir-ivory">
                    <img src={cat.image} alt={cat.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-[15px] font-semibold text-ophir-navy-deep leading-snug">{cat.title}</h3>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* B2B & INSTITUTIONAL SUPPLY */}
      <section className="bg-ophir-navy-deep">
        <div className="container-ophir py-16 lg:py-24">
          <FadeIn className="max-w-2xl">
            <SectionLabel light>B2B & Institutional Supply</SectionLabel>
            <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] lg:text-[42px] leading-tight text-white">Procurement solutions built for organizations that buy at scale</h2>
            <p className="mt-5 text-[16.5px] leading-relaxed text-white/75">
              Ophir Supply works with businesses, wholesalers, retailers and institutional buyers requiring dependable product sourcing, bulk purchasing and distribution support.
            </p>
          </FadeIn>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {HOMEPAGE_B2B.map((c, i) => (
              <FadeIn key={c.to} delay={(i % 4) * 0.05}>
                <Link to={c.to} className="group block h-full rounded-2xl border border-white/12 bg-white/[0.04] p-6 hover:bg-white/[0.07] transition-colors">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-ophir-gold/15 text-ophir-gold-soft"><Icon name={c.icon} size={20} /></span>
                  <h3 className="mt-5 font-serif text-[19px] text-white">{c.title}</h3>
                  <p className="mt-2.5 text-[14.5px] leading-relaxed text-white/70">{c.body}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-[13.5px] font-semibold text-ophir-gold-soft">Learn more <ArrowRight size={15} /></span>
                </Link>
              </FadeIn>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <OphirButton variant="gold" to="/institutional-buyers">Explore B2B Supply</OphirButton>
            <OphirButton variant="secondary" to="/request-bulk-quote" className="!bg-transparent !border-white/50 !text-white hover:!bg-white/10">Request a Bulk Quote</OphirButton>
          </div>
        </div>
      </section>

      {/* SUPPLIER VALUE (split) */}
      <section className="bg-white border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeIn>
              <div className="overflow-hidden rounded-2xl border border-ophir-grey shadow-ophir">
                <img src="/images/ophir/suppliers/review.webp" alt="A supplier representative and a purchasing professional reviewing product samples and wholesale information" loading="lazy" className="w-full h-full object-cover" />
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <SectionLabel>For manufacturers & suppliers</SectionLabel>
              <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] lg:text-[40px] leading-tight text-ophir-navy-deep">A professional home for your products</h2>
              <p className="mt-5 text-[17px] leading-relaxed text-ophir-muted">
                We review each opportunity responsibly and communicate clearly throughout. Our focus is authentic, long-term supply relationships — not shortcuts.
              </p>
              <ul className="mt-6 space-y-3">
                {['Professional, responsive communication', 'Responsible product & documentation evaluation', 'Respect for brand standards and approved channels', 'Long-term partnership thinking'].map((li) => (
                  <li key={li} className="flex items-start gap-3 text-[15px] text-ophir-navy">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-ophir-gold shrink-0" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <OphirButton to="/suppliers">Submit a Supplier Opportunity</OphirButton>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PROCESS PREVIEW */}
      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <FadeIn className="max-w-2xl mb-12">
            <SectionLabel>Our process</SectionLabel>
            <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] lg:text-[42px] leading-tight text-ophir-navy-deep">A clear, six-step path</h2>
          </FadeIn>
          <FadeIn><ProcessDiagram steps={process.length ? process : undefined} /></FadeIn>
        </div>
      </section>

      {/* GLOBAL FOCUS */}
      <section className="bg-white border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeIn>
              <SectionLabel>Markets</SectionLabel>
              <h2 className="mt-5 font-serif text-[30px] sm:text-[36px] lg:text-[40px] leading-tight text-ophir-navy-deep">Global Sourcing. North American Focus.</h2>
              <p className="mt-5 text-[17px] leading-relaxed text-ophir-muted">
                We source products across multiple markets while primarily serving suitable commercial opportunities in Canada and the United States. Our approach connects quality goods with appropriate North American demand.
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="rounded-2xl border border-ophir-grey bg-ophir-ivory-soft p-6 shadow-ophir">
                <MarketsMap className="w-full h-auto" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* RESPONSIBLE REPRESENTATION + FAQ */}
      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            <FadeIn>
              <SectionLabel>Responsibility</SectionLabel>
              <h2 className="mt-5 font-serif text-[28px] sm:text-[34px] leading-tight text-ophir-navy-deep">{responsible.heading}</h2>
              <p className="mt-5 text-[16px] leading-relaxed text-ophir-muted">{responsible.body}</p>
              <p className="mt-6 text-[13.5px] leading-relaxed text-ophir-muted bg-white border border-ophir-grey rounded-xl p-4">
                {responsible.disclaimer}
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h3 className="font-serif text-[22px] text-ophir-navy-deep mb-2">Common questions</h3>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-[15.5px] text-ophir-navy hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-ophir-muted">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="bg-white border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <FadeIn>
              <div className="h-full bg-ophir-ivory-soft border border-ophir-navy/15 rounded-2xl p-8 shadow-ophir">
                <div className="gold-rule w-12 mb-5" />
                <h3 className="font-serif text-[24px] text-ophir-navy-deep">For Manufacturers and Suppliers</h3>
                <p className="mt-3 text-[15.5px] leading-relaxed text-ophir-muted">Introduce your company, product line or wholesale programme to OPHIR SUPPLY for commercial review.</p>
                <div className="mt-6"><OphirButton to="/suppliers">Submit a Supplier Opportunity</OphirButton></div>
              </div>
            </FadeIn>
            <FadeIn delay={0.08}>
              <div className="h-full bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-8 shadow-ophir">
                <div className="gold-rule w-12 mb-5" />
                <h3 className="font-serif text-[24px] text-ophir-navy-deep">For Retailers and Commercial Buyers</h3>
                <p className="mt-3 text-[15.5px] leading-relaxed text-ophir-muted">Discuss suitable product categories, purchasing requirements and commercial supply opportunities.</p>
                <div className="mt-6"><OphirButton variant="secondary" to="/buyers">Make a Buyer Inquiry</OphirButton></div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* DARK CONVERSION */}
      <section className="navy-gradient">
        <div className="container-ophir py-16 lg:py-20 text-center">
          <FadeIn>
            <SectionLabel center light>Reliable Supply. Valuable Partnerships.</SectionLabel>
            <h2 className="mt-5 font-serif text-[30px] sm:text-[38px] lg:text-[44px] leading-tight text-white max-w-3xl mx-auto">
              Ready to introduce your products or discuss a supply opportunity?
            </h2>
            <p className="mt-5 text-[16px] text-white/75 max-w-xl mx-auto">
              Start a professional conversation with our team. We review each opportunity carefully and respond with clear next steps.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <OphirButton variant="gold" to="/suppliers">Become a Supplier</OphirButton>
              <OphirButton variant="secondary" to="/contact" className="!bg-transparent !border-white/50 !text-white hover:!bg-white/10">Contact Our Team</OphirButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
